import React, { useState, useEffect } from "react";

const PatientInsuranceForm = ({ onSubmit, onClose, initialData = {} }) => {
  // State variables 
  const [name, setName] = useState(initialData.name || ""); 
  const [companyName, setCompanyName] = useState(initialData.companyName || ""); 
  const [contactNumber, setContactNumber] = useState(initialData.contactNumber || ""); 
  const [hmoCard, setHmoCard] = useState(initialData.hmoCard || ""); 
  const [hmoAccountNumber, setHmoAccountNumber] = useState(initialData.hmoAccountNumber || ""); 
  const [validGovernmentID, setValidGovernmentID] = useState(initialData.validGovernmentID || ""); 
  const [validGovernmentIDNumber, setValidGovernmentIDNumber] = useState(initialData.validGovernmentIDNumber || ""); 
  const [birthdate, setBirthdate] = useState(initialData.birthdate || ""); 
  const [relationship, setRelationship] = useState(initialData.relationship || "");

  // Update form fields when initialData changes (for when user data loads after component mounts)
  useEffect(() => {
    if (initialData.name && !name) {
      setName(initialData.name);
    }
    if (initialData.contactNumber && !contactNumber) {
      setContactNumber(initialData.contactNumber);
    }
    if (initialData.birthdate && !birthdate) {
      setBirthdate(initialData.birthdate);
    }
  }, [initialData.name, initialData.contactNumber, initialData.birthdate]); 

  // Validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoingBack, setIsGoingBack] = useState(false);

  // Validation function
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'name':
        if (!value.trim()) return 'Patient name is required';
        return '';
      case 'companyName':
        if (!value.trim()) return 'Insurance company is required';
        return '';
      case 'contactNumber':
        if (!value.trim()) return 'Mobile number is required';
        if (value.length > 0 && !value.startsWith('09')) {
          return 'Mobile number must start with \'09\'';
        }
        if (value.length > 0 && value.length !== 11) {
          return 'Mobile number must be 11 digits (e.g., 09XXXXXXXXX)';
        }
        return '';
      case 'hmoCard':
        if (!value.trim()) return 'HMO card is required';
        return '';
      case 'hmoAccountNumber':
        if (!value.trim()) return 'HMO account number is required';
        return '';
      case 'validGovernmentID':
        if (!value.trim()) return 'Government ID type is required';
        return '';
      case 'validGovernmentIDNumber':
        if (!value.trim()) return 'Government ID number is required';
        return '';
      case 'birthdate':
        if (!value.trim()) return 'Birthdate is required';
        const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-(\d{4})$/;
        if (!dateRegex.test(value)) return 'Birthdate must be in MM-DD-YYYY format';
        const [month, day, year] = value.split('-').map(Number);
        const testDate = new Date(year, month - 1, day);
        if (testDate.getFullYear() !== year || testDate.getMonth() !== month - 1 || testDate.getDate() !== day) {
          return 'Please enter a valid date';
        }
        return '';
      case 'relationship':
        if (!value.trim()) return 'Relationship is required';
        return '';
      default:
        return '';
    }
  };

  // Handle field blur (when user leaves a field)
  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    const value = getFieldValue(fieldName);
    const error = validateField(fieldName, value);
    setErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  // Handle field change
  const handleFieldChange = (fieldName, value) => {
    // Update the corresponding state
    switch (fieldName) {
      case 'name':
        if (/^[a-zA-Z\s]*$/.test(value)) {
          setName(value.replace(/\b\w/g, (char) => char.toUpperCase()));
        }
        break;
      case 'companyName':
        setCompanyName(value);
        break;
      case 'contactNumber':
        if (/^\d*$/.test(value)) {
          setContactNumber(value);
        }
        break;
      case 'hmoCard':
        // Allow all characters: numbers, letters, special characters
        setHmoCard(value);
        break;
      case 'hmoAccountNumber':
        // Allow all characters: numbers, letters, special characters
        setHmoAccountNumber(value);
        break;
      case 'validGovernmentID':
        setValidGovernmentID(value);
        break;
      case 'validGovernmentIDNumber':
        // Allow all characters including special characters, letters, numbers, spaces, etc.
        // Government ID numbers can have various formats like:
        // - Numbers only: 123456789
        // - Letters and numbers: ABC123456
        // - With dashes: 123-456-789
        // - With spaces: 123 456 789
        // - With special characters: 123/456/789, 123.456.789, etc.
        setValidGovernmentIDNumber(value);
        break;
      case 'birthdate':
        setBirthdate(value);
        break;
      case 'relationship':
        setRelationship(value);
        break;
      default:
        break;
    }
    
    // Clear error if field is touched and valid
    if (touched[fieldName]) {
      const error = validateField(fieldName, value);
      setErrors(prev => ({ ...prev, [fieldName]: error }));
    }
  };

  // Helper function to get field value
  const getFieldValue = (fieldName) => {
    switch (fieldName) {
      case 'name': return name;
      case 'companyName': return companyName;
      case 'contactNumber': return contactNumber;
      case 'hmoCard': return hmoCard;
      case 'hmoAccountNumber': return hmoAccountNumber;
      case 'validGovernmentID': return validGovernmentID;
      case 'validGovernmentIDNumber': return validGovernmentIDNumber;
      case 'birthdate': return birthdate;
      case 'relationship': return relationship;
      default: return '';
    }
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mark all fields as touched
    setTouched({
      name: true,
      companyName: true,
      contactNumber: true,
      hmoCard: true,
      hmoAccountNumber: true,
      validGovernmentID: true,
      validGovernmentIDNumber: true,
      birthdate: true,
      relationship: true
    });

    // Validate all fields
    const newErrors = {};
    const fields = ['name', 'companyName', 'contactNumber', 'hmoCard', 'hmoAccountNumber', 'validGovernmentID', 'validGovernmentIDNumber', 'birthdate', 'relationship'];
    
    fields.forEach(field => {
      const value = getFieldValue(field);
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    // Check if there are any validation errors
    if (Object.keys(newErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    const formData = {
      name,
      companyName,
      contactNumber,
      hmoCard,
      hmoAccountNumber,
      validGovernmentID,
      validGovernmentIDNumber,
      birthdate,
      relationship,
    };
    onSubmit(formData);
    setIsSubmitting(false);
  };

  // Function to handle go back
  const handleGoBack = async () => {
    setIsGoingBack(true);
    
    // Add a small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 500));
    
    onClose(true);
    setIsGoingBack(false);
  };

  return (
    <div style={{ width: "100%" }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Patient Name */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "20px" }}>
          <label style={{ 
            fontSize: "14px", 
            fontWeight: "600", 
            color: "#333",
            marginBottom: "4px"
          }}>
            Patient Name *
          </label>
          <input 
            placeholder="Enter patient name"
            type="text"
            required
            value={name}
            maxLength="100"
            onChange={(e) => handleFieldChange('name', e.target.value)}
            style={{
              padding: "12px 16px",
              border: errors.name && touched.name ? "1px solid #f44336" : "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "#fff",
              transition: "border-color 0.2s ease",
              outline: "none"
            }}
            onFocus={(e) => e.target.style.borderColor = "#007bff"}
            onBlur={(e) => {
              e.target.style.borderColor = errors.name && touched.name ? "#f44336" : "#ddd";
              handleBlur('name');
            }}
          />
          {errors.name && touched.name && (
            <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
              {errors.name}
            </div>
          )}
        </div>
        
        {/* Company Name and Contact Number */}
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.5fr", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Insurance Company *
            </label>
            <select
              required
              value={companyName}
              onChange={(e) => handleFieldChange('companyName', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.companyName && touched.companyName ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none",
                cursor: "pointer"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.companyName && touched.companyName ? "#f44336" : "#ddd";
                handleBlur('companyName');
              }}
            >
              <option value="">Select insurance company</option>
              <option value="Intellicare (Your Intelligent Health Choice)">Intellicare (Your Intelligent Health Choice)</option>
              <option value="MediCard">MediCard</option>
              <option value="MaxiCare (Live Your Best Life)">MaxiCare (Live Your Best Life)</option>
              <option value="AVEGA">AVEGA</option>
              <option value="flexicare">flexicare</option>
              <option value="InLife (Insulare Health Care)">InLife (Insulare Health Care)</option>
              <option value="HMI (Health Maintenance, Inc.)">HMI (Health Maintenance, Inc.)</option>
              <option value="Lacson & Lacson (Insurance Brokers, Inc.)">Lacson & Lacson (Insurance Brokers, Inc.)</option>
              <option value="ValuCare (Healthcare You Can Count On)">ValuCare (Healthcare You Can Count On)</option>
              <option value="PhilCare (Healthcare Made Smarter)">PhilCare (Healthcare Made Smarter)</option>
              <option value="ASIANLIFE & GENERAL ASSURANCE CORPORATION">ASIANLIFE & GENERAL ASSURANCE CORPORATION</option>
              <option value="EastWest Healthcare (Securing a Healthy Horizon)">EastWest Healthcare (Securing a Healthy Horizon)</option>
              <option value="COCOLIFE HEALTHCARE">COCOLIFE HEALTHCARE</option>
              <option value="KAISER INTERNATIONAL (Health Group, Inc.)">KAISER INTERNATIONAL (Health Group, Inc.)</option>
              <option value="eTiQa Insurance">eTiQa Insurance</option>
              <option value="Life & Health (HMP Incorporated)">Life & Health (HMP Incorporated)</option>
              <option value="DNC (Dental Network Company)">DNC (Dental Network Company)</option>
              <option value="Health Partners Dental Access, Inc.">Health Partners Dental Access, Inc.</option>
              <option value="Getwell Health Systems">Getwell Health Systems</option>
              <option value="GENERAL">GENERAL</option>
            </select>
            {errors.companyName && touched.companyName && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.companyName}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Mobile Number *
            </label>
            <input 
              placeholder="Enter mobile number"
              type="text"
              required
              maxLength="11"
              value={contactNumber}
              onChange={(e) => handleFieldChange('contactNumber', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.contactNumber && touched.contactNumber ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.contactNumber && touched.contactNumber ? "#f44336" : "#ddd";
                handleBlur('contactNumber');
              }}
            />
            {errors.contactNumber && touched.contactNumber && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.contactNumber}
              </div>
            )}
          </div>
        </div>

        {/* HMO Card and HMO Account Number */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              HMO Card *
            </label>
            <input 
              placeholder="Enter HMO card number"
              type="text"
              required
              value={hmoCard}
              maxLength="50"
              onChange={(e) => handleFieldChange('hmoCard', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.hmoCard && touched.hmoCard ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.hmoCard && touched.hmoCard ? "#f44336" : "#ddd";
                handleBlur('hmoCard');
              }}
            />
            {errors.hmoCard && touched.hmoCard && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.hmoCard}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              HMO Account Number *
            </label>
            <input 
              placeholder="Enter HMO account number"
              type="text"
              required
              value={hmoAccountNumber}
              maxLength="50"
              onChange={(e) => handleFieldChange('hmoAccountNumber', e.target.value)}
                          style={{
              padding: "12px 16px",
              border: errors.hmoAccountNumber && touched.hmoAccountNumber ? "1px solid #f44336" : "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "#fff",
              transition: "border-color 0.2s ease",
              outline: "none"
            }}
            onFocus={(e) => e.target.style.borderColor = "#007bff"}
            onBlur={(e) => {
              e.target.style.borderColor = errors.hmoAccountNumber && touched.hmoAccountNumber ? "#f44336" : "#ddd";
              handleBlur('hmoAccountNumber');
            }}
            />
            {errors.hmoAccountNumber && touched.hmoAccountNumber && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.hmoAccountNumber}
              </div>
            )}
          </div>
        </div>

        {/* Valid Government ID and Valid Government ID Number */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Government ID Type *
            </label>
            <select
              required
              value={validGovernmentID}
              onChange={(e) => handleFieldChange('validGovernmentID', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.validGovernmentID && touched.validGovernmentID ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none",
                cursor: "pointer"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.validGovernmentID && touched.validGovernmentID ? "#f44336" : "#ddd";
                handleBlur('validGovernmentID');
              }}
            >
              <option value="">Select government ID type</option>
              <option value="Philippine Identification Card (PhilSys ID / National ID)">Philippine Identification Card (PhilSys ID / National ID)</option>
              <option value="Philippine Passport">Philippine Passport</option>
              <option value="Driver's License (LTO)">Driver's License (LTO)</option>
              <option value="Unified Multi-Purpose Identification Card (UMID)">Unified Multi-Purpose Identification Card (UMID)</option>
              <option value="Social Security System Identification Card (SSS ID)">Social Security System Identification Card (SSS ID)</option>
              <option value="Government Service Insurance System Electronic Card (GSIS eCard / GSIS ID)">Government Service Insurance System Electronic Card (GSIS eCard / GSIS ID)</option>
              <option value="Professional Regulation Commission Identification Card (PRC ID)">Professional Regulation Commission Identification Card (PRC ID)</option>
              <option value="Philippine Postal Identification Card (Postal ID)">Philippine Postal Identification Card (Postal ID)</option>
              <option value="Voter's Identification Card / Voter's Certification (Commission on Elections or COMELEC)">Voter's Identification Card / Voter's Certification (Commission on Elections or COMELEC)</option>
              <option value="National Bureau of Investigation Clearance (NBI Clearance)">National Bureau of Investigation Clearance (NBI Clearance)</option>
              <option value="Police Clearance Certificate">Police Clearance Certificate</option>
              <option value="Senior Citizen Identification Card (Senior Citizen ID)">Senior Citizen Identification Card (Senior Citizen ID)</option>
              <option value="Person with Disability Identification Card (PWD ID)">Person with Disability Identification Card (PWD ID)</option>
              <option value="Overseas Workers Welfare Administration Identification Card (OWWA ID / OWWA eCard)">Overseas Workers Welfare Administration Identification Card (OWWA ID / OWWA eCard)</option>
              <option value="Overseas Filipino Worker Identification Card (OFW ID)">Overseas Filipino Worker Identification Card (OFW ID)</option>
              <option value="Seafarer's Identification and Record Book (SIRB / Seaman's Book)">Seafarer's Identification and Record Book (SIRB / Seaman's Book)</option>
              <option value="Alien Certificate of Registration Identity Card (ACR I-Card)">Alien Certificate of Registration Identity Card (ACR I-Card)</option>
              <option value="Firearms License Identification Card (Philippine National Police or PNP Firearms ID)">Firearms License Identification Card (Philippine National Police or PNP Firearms ID)</option>
              <option value="Armed Forces of the Philippines Identification Card (AFP ID)">Armed Forces of the Philippines Identification Card (AFP ID)</option>
              <option value="Philippine Veterans Affairs Office Identification Card (PVAO ID)">Philippine Veterans Affairs Office Identification Card (PVAO ID)</option>
              <option value="Barangay Identification Card (Barangay ID)">Barangay Identification Card (Barangay ID)</option>
              <option value="Philippine Health Insurance Corporation Identification Card (PhilHealth ID)">Philippine Health Insurance Corporation Identification Card (PhilHealth ID)</option>
              <option value="Taxpayer Identification Number Identification Card (TIN ID / BIR ID)">Taxpayer Identification Number Identification Card (TIN ID / BIR ID)</option>
              <option value="Student Identification Card (Student ID)">Student Identification Card (Student ID)</option>
              <option value="Employee Identification Card (Employee ID)">Employee Identification Card (Employee ID)</option>
            </select>
            {errors.validGovernmentID && touched.validGovernmentID && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.validGovernmentID}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Government ID Number *
            </label>
            <input 
              placeholder="Enter government ID number (any format)"
              type="text"
              required
              value={validGovernmentIDNumber}
              maxLength="50"
              onChange={(e) => handleFieldChange('validGovernmentIDNumber', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.validGovernmentIDNumber && touched.validGovernmentIDNumber ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.validGovernmentIDNumber && touched.validGovernmentIDNumber ? "#f44336" : "#ddd";
                handleBlur('validGovernmentIDNumber');
              }}
            />
            {errors.validGovernmentIDNumber && touched.validGovernmentIDNumber && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.validGovernmentIDNumber}
              </div>
            )}
          </div>
        </div>

        {/* Birthdate and Relationship */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Birthdate *
            </label>
            <input 
              type="text"
              required
              placeholder="MM-DD-YYYY"
              value={birthdate}
              onChange={(e) => {
                let value = e.target.value.replace(/\D/g, '');
                if (value.length >= 2) {
                  value = value.slice(0, 2) + '-' + value.slice(2);
                }
                if (value.length >= 5) {
                  value = value.slice(0, 5) + '-' + value.slice(5, 9);
                }
                handleFieldChange('birthdate', value);
              }}
              style={{
                padding: "12px 16px",
                border: errors.birthdate && touched.birthdate ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.birthdate && touched.birthdate ? "#f44336" : "#ddd";
                handleBlur('birthdate');
              }}
            />
            {errors.birthdate && touched.birthdate && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.birthdate}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#333",
              marginBottom: "4px"
            }}>
              Relationship *
            </label>
            <select
              required
              value={relationship}
              onChange={(e) => handleFieldChange('relationship', e.target.value)}
              style={{
                padding: "12px 16px",
                border: errors.relationship && touched.relationship ? "1px solid #f44336" : "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "#fff",
                transition: "border-color 0.2s ease",
                outline: "none",
                cursor: "pointer"
              }}
              onFocus={(e) => e.target.style.borderColor = "#007bff"}
              onBlur={(e) => {
                e.target.style.borderColor = errors.relationship && touched.relationship ? "#f44336" : "#ddd";
                handleBlur('relationship');
              }}
            >
              <option value="">Select relationship</option>
              <option value="Principal">Principal</option>
              <option value="Dependent">Dependent</option>
            </select>
            {errors.relationship && touched.relationship && (
              <div style={{ color: "#f44336", fontSize: 12, marginTop: 4 }}>
                {errors.relationship}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          gap: "16px", 
          marginTop: "20px",
          paddingTop: "20px",
          borderTop: "1px solid #eee"
        }}>
          <button 
            type="submit"
            disabled={isSubmitting || isGoingBack}
            style={{
              padding: "12px 32px",
              backgroundColor: (isSubmitting || isGoingBack) ? "#ccc" : "#C5A572",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: (isSubmitting || isGoingBack) ? "not-allowed" : "pointer",
              transition: "background-color 0.2s ease",
              minWidth: "120px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "#B8955A";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "#C5A572";
              }
            }}
          >
            {isSubmitting ? (
              <>
                <div style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #ffffff",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}></div>
                <span>Submitting...</span>
              </>
            ) : (
              "Submit"
            )}
          </button>
          <button 
            type="button" 
            onClick={handleGoBack}
            disabled={isSubmitting || isGoingBack}
            style={{
              padding: "12px 32px",
              backgroundColor: (isSubmitting || isGoingBack) ? "#f5f5f5" : "transparent",
              color: (isSubmitting || isGoingBack) ? "#999" : "#C5A572",
              border: `2px solid ${(isSubmitting || isGoingBack) ? "#ddd" : "#C5A572"}`,
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: (isSubmitting || isGoingBack) ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              minWidth: "120px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "#C5A572";
                e.target.style.color = "#FDF9F3";
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting && !isGoingBack) {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#C5A572";
              }
            }}
          >
            {isGoingBack ? (
              <>
                <div style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #C5A572",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}></div>
                <span>Going Back...</span>
              </>
            ) : (
              "Go Back"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PatientInsuranceForm;


